import { useContext, useEffect, useState } from 'react'

import {
  Button,
  ChevronDownIcon,
  Dialog,
  DropdownMenu,
  Flex,
  Heading,
  Text,
} from '@buffer-mono/popcorn'

import { ProfessionalCard } from './components/ProfessionalCard'
import { PersonalProfile } from './components/PersonalProfile/PersonalProfile'
import { PersonalProfileCard } from './components/PersonalProfileCard'
import PaywallModal from '../PaywallPrompt/PaywallPrompt'
import { Header } from './components/Header/Header'
import { Footer } from './components/Footer/Footer'

import { trackProfileTypeSelectionViewed } from '../../tracking'
import {
  InstagramAuthMethod,
  type onRedirectParams,
  Service,
} from '../../types'
import { UserContext, useUser } from '../../../../../common/context/User'
import { isOneBufferOrganization } from '../../../../../common/utils/user'
import { Notifications } from './components/Notifications/Notifications'
import { setConnectionSuccessQueryParamsForCustomChannel } from '../../utils'
import { useDispatch } from 'react-redux'
import { resetChannelConnectionsState } from '../../../store/channelConnectionsReducer'
import type { AccountType } from '../../../../../common/events/types'

import styles from './InstagramSelectAccountType.module.css'
import footerStyles from './components/Footer/Footer.module.css'
import { useModalManager } from '../../../../../components/ModalManager/hooks/useModalManager'
import useAuthorizationInfo from '../../hooks/useAuthorizationInfo/useAuthorizationInfo'

enum Steps {
  accountType = 'accountType',
  notifications = 'notifications',
  personal = 'personal',
}

function openPublishSuccessScreen(channelId: string): void {
  window.location.href =
    setConnectionSuccessQueryParamsForCustomChannel(channelId).toString()
}

export default function InstagramSelectAccountType({
  onRedirect,
}: {
  onRedirect: (args: onRedirectParams) => void
}): JSX.Element {
  const dispatch = useDispatch()
  const user = useContext(UserContext)
  const { modalData: data } = useModalManager()

  const [showPayWallModal, setShowPayWallModal] = useState(false)
  const [selectedAccountType, setSelectedAccountType] = useState<
    AccountType | Steps
  >(data?.accountType || Steps.accountType)
  const [channelId, setChannelId] = useState<string | null>(null)
  const isOneBufferOrg = isOneBufferOrganization(user)

  if (showPayWallModal) {
    return (
      <PaywallModal
        service={Service.instagram}
        authMethod={InstagramAuthMethod.notificationsOnly}
        onClose={(): void => setShowPayWallModal(false)}
      />
    )
  }

  const stepMap = {
    personal: (
      <PersonalProfile
        onBack={(): void => setSelectedAccountType(Steps.accountType)}
        onContinue={(channelId): void => {
          setChannelId(channelId)
          setSelectedAccountType(Steps.notifications)
        }}
      />
    ),
    notifications: (
      <Notifications
        onContinue={(): void => {
          // Redirect to '/channels' and trigger channel connection success modal
          if (channelId) {
            openPublishSuccessScreen(channelId)
            dispatch(resetChannelConnectionsState({}))
          }
        }}
      />
    ),
    accountType: (
      <SelectAccountType
        onPersonalSelect={(): void => {
          if (isOneBufferOrg) {
            setSelectedAccountType(Steps.personal)
          } else {
            setShowPayWallModal(true)
          }
        }}
        onRedirect={onRedirect}
      />
    ),
  }

  return stepMap[selectedAccountType as Steps]
}

function SelectAccountType({
  onPersonalSelect,
  onRedirect,
}: {
  onPersonalSelect: () => void
  onRedirect: (args: onRedirectParams) => void
}): JSX.Element {
  const account = useUser()

  const { handleGetAuthorizationInfo, url } = useAuthorizationInfo()

  useEffect(() => {
    handleGetAuthorizationInfo({
      service: Service.instagram,
      authMethod: InstagramAuthMethod.facebookLogin,
      isRefreshingConnection: false,
    })
  }, [handleGetAuthorizationInfo])

  useEffect(() => {
    trackProfileTypeSelectionViewed({
      account,
      service: Service.instagram,
    })
  }, [account])

  return (
    <>
      <Header />

      <Dialog.Body className={styles.body}>
        <div className={styles.titleSubtitle}>
          <Heading size="large" align="center">
            Which type of Instagram account would you like to connect?
          </Heading>
          <Text className={styles.subtitle}>
            The account type you choose will determine the features available to
            you.
          </Text>
        </div>
        <Flex justify="center" gap="xl">
          <PersonalProfileCard onPersonalSelect={onPersonalSelect} />
          <ProfessionalCard onRedirect={onRedirect} />
        </Flex>
      </Dialog.Body>
      <Footer>
        <DropdownMenu
          align="start"
          className={footerStyles.menu}
          trigger={
            <Button
              variant="tertiary"
              size="large"
              className={footerStyles.helpButton}
            >
              Help
              <ChevronDownIcon />
            </Button>
          }
        >
          <DropdownMenu.Item asChild>
            <a
              className={footerStyles.helpLink}
              href="https://support.buffer.com/article/554-using-instagram-with-buffer#Finding-your-Instagram-account-type-x-zBM?utm_source=buffer&utm_medium=learn-more-link&utm_campaign=learn-more"
              target="_blank"
              rel="noopener noreferrer"
            >
              Finding your Instagram account type
            </a>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <a
              className={footerStyles.helpLink}
              href="https://support.buffer.com/article/554-using-instagram-with-buffer#Converting-your-Instagram-account-to-a-Professional-account-Creator-o-pCMTs?utm_source=buffer&utm_medium=learn-more-link&utm_campaign=learn-more"
              target="_blank"
              rel="noopener noreferrer"
            >
              Converting your Instagram account to a Professional account
            </a>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <button
              className={footerStyles.helpLink}
              onClick={(): void => {
                if (url) {
                  onRedirect({
                    redirectUrl: url,
                    authMethod: InstagramAuthMethod.facebookLogin,
                  })
                }
              }}
            >
              Connect Instagram via linked Facebook Page
            </button>
          </DropdownMenu.Item>
        </DropdownMenu>
      </Footer>
    </>
  )
}
